import React, { useEffect, useRef } from 'react';

interface ParticleProps {
  color?: string;
  particleCount?: number;
  lineOpacity?: number;
  particleOpacity?: number;
  particleSize?: number;
  speed?: number;
  connectDistance?: number;
  className?: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
}

/**
 * ParticleBackground Component
 * 
 * A simplified but reliable particle network background effect
 */
const ParticleBackground: React.FC<ParticleProps> = ({
  color = '#4876ff',
  particleCount = 80,
  lineOpacity = 0.3,
  particleOpacity = 0.7,
  particleSize = 2,
  speed = 0.5,
  connectDistance = 150,
  className = "fixed inset-0 -z-10"
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  
  // Initialize particles and start animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size to match window
    const resizeCanvas = () => {
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        
        // Reinitialize particles when canvas is resized
        initParticles();
      }
    };
    
    // Create particles
    const initParticles = () => {
      if (!canvas) return;
      
      const particles: Particle[] = [];
      
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * speed,
          vy: (Math.random() - 0.5) * speed,
          size: Math.random() * particleSize + 1
        });
      }
      
      particlesRef.current = particles;
    };
    
    // Animation loop
    const animate = () => {
      if (!canvas || !ctx) return;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const particles = particlesRef.current;
      
      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        
        // Move particle
        p.x += p.vx;
        p.y += p.vy;
        
        // Bounce off edges
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        // Keep particles on screen
        p.x = Math.max(0, Math.min(canvas.width, p.x));
        p.y = Math.max(0, Math.min(canvas.height, p.y));
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `${color}${Math.floor(particleOpacity * 255).toString(16).padStart(2, '0')}`;
        ctx.fill();
        
        // Draw connections
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < connectDistance) {
            // Opacity based on distance
            const opacity = (1 - distance / connectDistance) * lineOpacity;
            const hexOpacity = Math.floor(opacity * 255).toString(16).padStart(2, '0');
            
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `${color}${hexOpacity}`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      
      // Continue animation
      animationRef.current = requestAnimationFrame(animate);
    };
    
    // Initial setup
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Start animation
    animate();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, [color, particleCount, particleOpacity, particleSize, lineOpacity, speed, connectDistance]);
  
  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default React.memo(ParticleBackground);
